// Data space config enums

"use strict";

import { defineAsyncComponent } from "vue";
import { AuthController } from "@/control/auth";
import ComponentLoader from "@/components/utils/ComponentLoader.vue";
import type { DataSpaceConfigStepStatus } from "@/api/definitions";

export type DataSpaceConfigSectionName =
    | "done"
    | "unknown"
    | "features"
    | "identity"
    | "tls_credentials"
    | "tls_credentials_modify"
    | "ca"
    | "wallet"
    | "wallet_manage"
    | "blockchain"
    | "blockchain_manage"
    | "did"
    | "did_public_key_registry"
    | "did_attestators"
    | "attestation"
    | "attestation_manage"
    | "catalog_broker"
    | "catalog_broker_request"
    | "clearing_house"
    | "clearing_house_config"
    | "marketplace_providers";

function lazySection(loader: () => Promise<any>) {
    return defineAsyncComponent({
        loader,
        loadingComponent: ComponentLoader,
        delay: 1000,
    });
}

// Every step that has a component of its own. The ones left out ('done', 'unknown')
// are rendered by whichever wizard shell (onboarding) is hosting the section instead
export const SECTION_COMPONENTS: Partial<Record<DataSpaceConfigSectionName, any>> = {
    features: lazySection(() => import("@/components/modals/ds-config/sections/SelectFeatures.vue")),
    identity: lazySection(() => import("@/components/modals/ds-config/sections/IdentityMethods.vue")),
    tls_credentials: lazySection(() => import("@/components/modals/ds-config/sections/CreateTlsCredentials.vue")),
    tls_credentials_modify: lazySection(() => import("@/components/modals/ds-config/sections/ModifyTlsCredentials.vue")),
    ca: lazySection(() => import("@/components/modals/ds-config/sections/CertificationAuthorities.vue")),
    wallet: lazySection(() => import("@/components/modals/ds-config/sections/CreateWallet.vue")),
    wallet_manage: lazySection(() => import("@/components/modals/ds-config/sections/WalletsManage.vue")),
    blockchain: lazySection(() => import("@/components/modals/ds-config/sections/AddBlockchainNetwork.vue")),
    blockchain_manage: lazySection(() => import("@/components/modals/ds-config/sections/BlockchainManage.vue")),
    did: lazySection(() => import("@/components/modals/ds-config/sections/DidConfig.vue")),
    did_public_key_registry: lazySection(() => import("@/components/modals/ds-config/sections/DidPublicKeyRegistry.vue")),
    did_attestators: lazySection(() => import("@/components/modals/ds-config/sections/TrustedAttestators.vue")),
    attestation: lazySection(() => import("@/components/modals/ds-config/sections/ObtainAttestation.vue")),
    attestation_manage: lazySection(() => import("@/components/modals/ds-config/sections/AttestationsManage.vue")),
    catalog_broker: lazySection(() => import("@/components/modals/ds-config/sections/CatalogProviders.vue")),
    catalog_broker_request: lazySection(() => import("@/components/modals/ds-config/sections/RequestBrokerRegistration.vue")),
    marketplace_providers: lazySection(() => import("@/components/modals/ds-config/sections/MarketplaceProviders.vue")),
    clearing_house: lazySection(() => import("@/components/modals/ds-config/sections/TrustedClearingHouses.vue")),
    clearing_house_config: lazySection(() => import("@/components/modals/ds-config/sections/ClearingHouseConfig.vue")),
};

/**
 * Renders data space step
 * @param s The step
 * @param $t The translation function
 * @returns The step name
 */
// Steps the admin is allowed to decline: each one carries its own "Skip" control inside its
// section. It matters beyond the badge in the checklist, because a data space with only these
// pending is operative: that is what tells a first run apart from a connector that has been
// working for months without opting into an optional feature.
const OPTIONAL_STEPS: Set<DataSpaceConfigSectionName> = new Set([
    "ca",
    "did_attestators",
    "catalog_broker",
    "catalog_broker_request",
    "clearing_house",
    "clearing_house_config",
    "marketplace_providers",
]);

export function isOptionalDataSpaceConfigStep(s: DataSpaceConfigSectionName): boolean {
    return OPTIONAL_STEPS.has(s);
}

/**
 * Checks whether something the data space cannot work without is still missing.
 * An empty or absent list answers no on purpose: the banner can still warn from
 * `configured`, but nothing should take over the screen on data we do not have.
 * @param steps The per-step status reported by the backend
 * @returns True if a step that cannot be skipped is still not configured
 */
export function hasMandatoryStepPending(steps: DataSpaceConfigStepStatus[]): boolean {
    return (steps || []).some((s) => !s.configured && !OPTIONAL_STEPS.has(s.id));
}

export function renderDataSpaceConfigStep(s: DataSpaceConfigSectionName, $t: (k: string) => string): string {
    switch (s) {
        case "features":
            return $t("Select data space connector features");
        case "identity":
            return $t("Identity methods");
        case "tls_credentials":
            return $t("Create TLS credentials");
        case "tls_credentials_modify":
            return $t("TLS Credentials");
        case "ca":
            return $t("Certification authorities");
        case "wallet":
            return $t("Create wallet");
        case "wallet_manage":
            return $t("Wallets");
        case "blockchain":
            return $t("Add a blockchain network");
        case "blockchain_manage":
            return $t("Blockchain networks");
        case "did":
            return $t("Configure DID");
        case "did_public_key_registry":
            return $t("Register public key into DID document");
        case "did_attestators":
            return $t("Trusted DID attestators");
        case "attestation":
            return $t("Verifiable credential / attestation");
        case "attestation_manage":
            return $t("Verifiable credentials / attestations");
        case "catalog_broker":
            return $t("Catalog providers and brokers");
        case "catalog_broker_request":
            return $t("Data provider registration");
        case "clearing_house":
            return $t("Trusted clearing houses");
        case "clearing_house_config":
            return $t("Clearing house blockchain configuration");
        case "marketplace_providers":
            return $t("App marketplace providers");
        case "done":
            return $t("Done!");
        default:
            return "???";
    }
}

// Checks if identity method needs CAs to work
function identityMethodNeedsCas(m: string): boolean {
    return ["x509"].includes(m);
}

// Checks if identity method needs wallets to work
function identityMethodNeedsWallets(m: string): boolean {
    return ["did:ethr"].includes(m);
}

// Checks if identity method needs blockchains to work
function identityMethodNeedsBlockchain(m: string): boolean {
    return ["did:ethr"].includes(m);
}

// Checks if identity method needs attestations (any DID)
function identityMethodNeedsAttestations(m: string): boolean {
    return m.startsWith("did:");
}

// Builds the full list of steps the process goes through, in order. Some entries have a
// "first visit" and a "manage" variant, chosen from the latest step, so every list built
// for the same latest step is consistent with the others
function buildDataSpaceConfigStepList(latest: DataSpaceConfigSectionName): DataSpaceConfigSectionName[] {
    const sections: DataSpaceConfigSectionName[] = [
        "features",
        "identity",
        latest === "tls_credentials" ? "tls_credentials" : "tls_credentials_modify",
    ];

    const enabledIdentityMethods = AuthController.GatewayIdentity.enabledMethods;
    const chosenIdentityMethod = AuthController.GatewayIdentity.method;

    if (enabledIdentityMethods.filter(identityMethodNeedsCas).length > 0) {
        sections.push("ca");
    }

    if (identityMethodNeedsWallets(chosenIdentityMethod)) {
        sections.push(latest === "wallet" ? "wallet" : "wallet_manage");
    }

    if (enabledIdentityMethods.filter(identityMethodNeedsBlockchain).length > 0) {
        sections.push(latest === "blockchain" ? "blockchain" : "blockchain_manage");
    }

    if (chosenIdentityMethod === "did:ethr") {
        // Specific steps for Ethereum DID
        sections.push("did");
        sections.push("did_public_key_registry");
    }

    if (enabledIdentityMethods.filter(identityMethodNeedsAttestations).length > 0) {
        sections.push("did_attestators");
    }

    if (identityMethodNeedsAttestations(chosenIdentityMethod)) {
        sections.push(latest === "attestation" ? "attestation" : "attestation_manage");
    }

    // The backend only reports these two when they apply (a provider registers into a broker,
    // only a clearing house configures itself). Listing them regardless left a step in the
    // checklist that no status would ever mark as completed, not even after skipping it
    sections.push("catalog_broker");

    if (!AuthController.IsBroker || latest === "catalog_broker_request") {
        sections.push("catalog_broker_request");
    }

    sections.push("clearing_house");

    if (AuthController.ClearingHouseEnabled || latest === "clearing_house_config") {
        sections.push("clearing_house_config");
    }

    if (AuthController.UseAppMarketplaces || latest === "marketplace_providers") {
        sections.push("marketplace_providers");
    }

    sections.push("done");

    return sections;
}

/**
 * Gets the step list for the data space configuration,
 * based on the latest step
 * @param latest The latest step
 * @param includeUpcoming True to also include the steps after the latest one, eg. to count
 * how many steps the whole process has. The step variants still depend on the latest step,
 * so both lists stay consistent with each other
 * @returns Tbe step list
 */
export function setupDataSpaceConfigStepList(latest: DataSpaceConfigSectionName, includeUpcoming?: boolean): DataSpaceConfigSectionName[] {
    const sections = buildDataSpaceConfigStepList(latest);

    const i = sections.indexOf(latest);

    if (i === -1) {
        return [];
    }

    return includeUpcoming ? sections : sections.slice(0, i + 1);
}
